/**
 * DemoPublicaBanner
 * Franja de la demo pública (acceso sin login), dividida en dos: compra (60 %) y prueba gratis (40 %).
 * Va arriba del topbar. Solo se monta para la cuenta demo compartida (`user.esCuentaDemo`);
 * el banner "Estás en modo demo" de DemoBanner sigue siendo para quien se registró.
 *
 * Jerarquía: "Comprar ahora" es la única acción con botón (turquesa sólido, con pulso).
 * La prueba gratis es una pregunta + un enlace, en color secundario.
 *
 * Puntos de corte (los del resto del proyecto):
 *  - < 768 px (celular): dos filas.
 *      Fila 1: badge + "Comprar ahora".
 *      Fila 2: "¿Con tu propia empresa?" + "Probar gratis →".
 *  - 768–1535 px: versión compacta (banner 38 px, botón 26 px).
 *      · 768–1023 px: compra sin mensaje · prueba solo con el enlace.
 *      · 1024–1279 px (lg): mensaje de compra corto · pregunta corta.
 *      · 1280–1535 px (xl): mensaje de compra completo · pregunta completa.
 *  - ≥ 1536 px (2xl): versión original (banner 44 px, botón 30 px), textos completos.
 *  Ningún texto se corta: o cabe completo, o se usa su versión corta, o no se muestra.
 */

import type { CSSProperties, ReactNode, SVGProps } from 'react';

export type DemoPublicaBannerMessage = 'a' | 'b' | 'c';

export interface DemoPublicaBannerProps {
  /** Compra del plan. Mientras no exista destino, el botón no hace nada. */
  onComprar?: () => void;
  /** Registro de cuenta propia en Senciyo */
  onRegistrar: () => void;
  /** Texto del enlace de prueba */
  trialLabel?: string;
  /** Mensaje de compra: "a" (recomendado) · "b" · "c" */
  message?: DemoPublicaBannerMessage;
  /** Distancia en px del borde izquierdo al badge "Explorando demo" (por defecto 56) */
  badgeInset?: number;
}

interface BuyCopy {
  /** Texto completo (≥ 1280 px) */
  full: ReactNode;
  /** Texto corto (1024–1279 px) */
  short: ReactNode;
}

const BUY_MESSAGES: Record<DemoPublicaBannerMessage, BuyCopy> = {
  a: {
    full: (
      <>
        Activa tu plan <strong>hoy mismo</strong> y empieza a vender.
      </>
    ),
    short: (
      <>
        <strong>Empieza a vender</strong> hoy.
      </>
    ),
  },
  b: {
    full: (
      <>
        <strong>Ventas, comprobantes y caja</strong> en un solo lugar.
      </>
    ),
    short: (
      <>
        <strong>Todo tu negocio</strong> en un lugar.
      </>
    ),
  },
  c: {
    full: (
      <>
        <strong>Empieza hoy</strong> y emite tus comprobantes con tu propia cuenta.
      </>
    ),
    short: (
      <>
        <strong>Empieza hoy</strong> mismo.
      </>
    ),
  },
};

function CartIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <circle cx="9" cy="20" r="1" />
      <circle cx="18" cy="20" r="1" />
      <path d="M2 3h3l2.7 12.4a2 2 0 0 0 2 1.6h8.1a2 2 0 0 0 2-1.5L21 8H6" />
    </svg>
  );
}

function ArrowIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/* ---------- Clases compartidas (compacta por defecto, original con 2xl:) ---------- */

const half =
  'flex min-w-0 items-center gap-2.5 px-3 py-1 ' +
  'md:justify-center md:px-5 ' +
  '2xl:gap-3 2xl:py-1.5';

/** Textos secundarios: `caption` (12 px) en la versión compacta, `ui-sm` (13 px) en la original */
const text = 'm-0 min-w-0 truncate text-caption font-normal text-demo-banner-soft 2xl:text-ui-sm 2xl:font-normal';

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-hover';

export const DemoPublicaBanner = ({
  onComprar,
  onRegistrar,
  trialLabel = 'Probar gratis',
  message = 'a',
  badgeInset = 56,
}: DemoPublicaBannerProps) => {
  const buy = BUY_MESSAGES[message] ?? BUY_MESSAGES.a;

  return (
    <div
      role="region"
      aria-label="Estás explorando una demo pública"
      style={{ '--demo-banner-inset': `${badgeInset}px` } as CSSProperties}
      className="relative border-b border-demo-banner bg-demo-banner font-sans text-demo-banner"
    >
      <div className="grid min-h-[38px] grid-cols-[auto_minmax(0,1fr)] md:grid-cols-[3fr_2fr] 2xl:min-h-11">
        {/* Badge: en celular ocupa la primera celda de la fila 1;
            desde 768 px se fija a la izquierda, a la altura del logo del topbar */}
        <span
          className={
            'ml-3 inline-flex h-5 shrink-0 items-center gap-1.5 self-center whitespace-nowrap rounded-full bg-white/10 px-[9px] text-caption-medium leading-none text-demo-banner ' +
            'md:absolute md:left-[var(--demo-banner-inset)] md:top-1/2 md:ml-0 md:-translate-y-1/2 ' +
            '2xl:h-[22px] 2xl:gap-[7px] 2xl:px-2.5'
          }
        >
          <span
            aria-hidden="true"
            className="h-[7px] w-[7px] rounded-full bg-accent animate-demo-dot motion-reduce:animate-none"
          />
          Explorando demo
        </span>

        {/* Mitad de compra (60 %). Desde 768 px reserva espacio a la izquierda para el badge */}
        <div className={`${half} justify-end md:pl-[calc(var(--demo-banner-inset)+150px)] 2xl:pl-[calc(var(--demo-banner-inset)+160px)]`}>
          <p className={`${text} hidden xl:block [&_strong]:font-semibold [&_strong]:text-accent`}>{buy.full}</p>
          <p className={`${text} hidden lg:block xl:hidden [&_strong]:font-semibold [&_strong]:text-accent`}>{buy.short}</p>

          <button
            type="button"
            onClick={onComprar}
            className={
              'inline-flex h-[26px] shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--radius-sm)] border-0 px-3 ' +
              'bg-accent text-ui-sm font-semibold text-accent-ink transition-colors hover:bg-accent-hover ' +
              'animate-demo-pulse motion-reduce:animate-none 2xl:h-[30px] 2xl:px-3.5 ' +
              focusRing
            }
          >
            <CartIcon className="h-3.5 w-3.5 shrink-0 2xl:h-[15px] 2xl:w-[15px]" />
            Comprar ahora
          </button>
        </div>

        {/* Mitad de prueba (40 %): pregunta + enlace. En celular ocupa toda la fila 2 */}
        <div className={`${half} col-span-2 justify-center border-t border-white/[.12] md:col-span-1 md:border-l md:border-t-0`}>
          <p className={`${text} hidden xl:block`}>¿Quieres usarlo con tu propia empresa?</p>
          <p className={`${text} md:hidden lg:block xl:hidden`}>¿Con tu propia empresa?</p>

          <button
            type="button"
            onClick={onRegistrar}
            className={
              'group inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-sm border-0 bg-transparent p-0 text-ui-sm font-medium text-demo-banner ' +
              'underline-offset-[3px] hover:underline ' +
              focusRing
            }
          >
            {trialLabel}
            <ArrowIcon className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
          </button>
        </div>
      </div>
    </div>
  );
};
